/**
 * DependencyPanel — Tools › Dependency Analyzer (top-level tab). Finds every place
 * an object is used (inbound) and what it uses (outbound), exhaustively — including
 * deep inside stored procedure/function, trigger, view and event bodies — across
 * one schema or the whole server. Also "where is this database name used?". The
 * matching core is server-side (internal/depends); this is the input + results UI.
 * Opened from Tools or by right-clicking an object/database in the browser
 * (props.initial pre-fills + auto-runs). See DEPENDENCY_ANALYZER_SPEC.md.
 */
import { createSignal, createResource, createMemo, onMount, Show, For } from 'solid-js';
import { api } from '../lib/api.js';
import { analyzeDependencies } from '../lib/dependencies.js';
import { toast } from '../lib/toast.js';

const KINDS = ['table', 'view', 'procedure', 'function', 'event', 'database'];
// Result kinds we can open in an editor: code objects → ScriptEditor, tables → the
// table editor. (trigger / database have no direct editor, so their rows aren't clickable.)
const OPENABLE = ['procedure', 'function', 'view', 'event', 'table'];

export default function DependencyPanel(props) {
  const init = props.initial || {};
  const [conns] = createResource(async () => {
    try { return (await api.get('/api/connections')).connections || []; } catch { return []; }
  });
  const [connId, setConnId] = createSignal(init.connectionId ? String(init.connectionId) : '');
  const [database, setDatabase] = createSignal(init.database || '');
  const [kind, setKind] = createSignal(init.kind || 'table');
  const [name, setName] = createSignal(init.name || '');
  const [scope, setScope] = createSignal(init.scope || 'schema');
  const [direction, setDirection] = createSignal(init.direction || 'both');
  const [running, setRunning] = createSignal(false);
  const [result, setResult] = createSignal(null);
  const [filter, setFilter] = createSignal('');

  const isDbMode = () => kind() === 'database';
  const connName = () => (conns() || []).find((c) => String(c.id) === String(connId()))?.name || '';
  // Click a result → open it in a floating editor (only the openable kinds).
  const openRow = (d) => {
    if (!props.onOpen || !OPENABLE.includes(d.kind)) return;
    props.onOpen({ connectionId: connId(), connectionName: connName(), database: d.database, kind: d.kind, name: d.name });
  };

  const run = async () => {
    if (!connId() || !name().trim()) { toast.error('Pick a connection and enter an object (or database) name.'); return; }
    if (!isDbMode() && scope() === 'schema' && !database().trim()) { toast.error('Enter the database for a schema-scope analysis.'); return; }
    setRunning(true); setResult(null);
    try {
      const r = await analyzeDependencies(connId(), {
        scope: scope(),
        database: isDbMode() ? name().trim() : database().trim(),
        kind: kind(), name: name().trim(),
        direction: isDbMode() ? 'inbound' : direction(),
      });
      setResult(r);
    } catch (e) { toast.error('Analyze failed: ' + (e?.message || e)); }
    finally { setRunning(false); }
  };

  // Auto-run when opened from a right-click (connection + name pre-filled).
  onMount(() => { if (init.connectionId && init.name) run(); });

  const apply = (list) => {
    const f = filter().trim().toLowerCase();
    const arr = list || [];
    if (!f) return arr;
    return arr.filter((d) => `${d.kind} ${d.database} ${d.name} ${d.detail || ''}`.toLowerCase().includes(f));
  };
  const grouped = (list) => {
    const g = {};
    for (const d of apply(list)) (g[d.kind] ||= []).push(d);
    return Object.entries(g).sort((a, b) => a[0].localeCompare(b[0]));
  };
  const inbound = createMemo(() => grouped(result()?.inbound));
  const outbound = createMemo(() => grouped(result()?.outbound));
  const total = (list) => (list || []).length;

  const Section = (sp) => (
    <div class="dep-section">
      <div class="dep-section-head">{sp.title} <span class="muted">({total(sp.list)})</span></div>
      <Show when={apply(sp.list).length === 0}>
        <div class="muted" style="padding:6px 10px;font-size:12px;">{total(sp.list) ? 'No matches for the filter.' : 'None found.'}</div>
      </Show>
      <For each={sp.groups}>
        {([k, items]) => (
          <div class="dep-group">
            <div class="dep-group-title mono">{k} <span class="muted">({items.length})</span></div>
            <For each={items}>
              {(d) => (
                <div class="dep-row" classList={{ 'dep-clickable': !!props.onOpen && OPENABLE.includes(d.kind) }}
                     title={!!props.onOpen && OPENABLE.includes(d.kind) ? `Open ${d.kind} ${d.database}.${d.name} in a floating editor` : undefined}
                     onClick={() => openRow(d)}>
                  <span class="dep-db mono">{d.database}</span>
                  <span class="dep-name mono">{d.name}</span>
                  <span class="dep-via" classList={{ 'dep-via-fk': d.via === 'fk' }}>{d.via}</span>
                  <Show when={d.detail}><span class="dep-detail muted mono">{d.detail}</span></Show>
                </div>
              )}
            </For>
          </div>
        )}
      </For>
    </div>
  );

  return (
    <div class="dep-panel">
      <div class="dep-toolbar">
        <Show when={props.onBack}><button class="btn btn-sm btn-ghost" onClick={() => props.onBack()}>← Back</button></Show>
        <select class="input" style="max-width:160px;" value={connId()} onChange={(e) => setConnId(e.currentTarget.value)} disabled={running()}>
          <option value="">— connection —</option>
          <For each={conns() || []}>{(c) => <option value={c.id} selected={String(c.id) === connId()}>{c.name}</option>}</For>
        </select>
        <select class="input" style="max-width:120px;" value={kind()} onChange={(e) => setKind(e.currentTarget.value)} disabled={running()}>
          <For each={KINDS}>{(k) => <option value={k} selected={k === kind()}>{k}</option>}</For>
        </select>
        <Show when={!isDbMode()}>
          <input class="input" style="max-width:150px;" placeholder="database" value={database()} onInput={(e) => setDatabase(e.currentTarget.value)} disabled={running()} />
        </Show>
        <input class="input" style="max-width:180px;" placeholder={isDbMode() ? 'database name' : 'object name'} value={name()} onInput={(e) => setName(e.currentTarget.value)} disabled={running()}
               onKeyDown={(e) => { if (e.key === 'Enter') run(); }} />
        <div class="fleet-mode-toggle" title="Schema = just this database. Server-wide = every database (slower).">
          <button class="btn btn-sm" classList={{ 'btn-primary': scope() === 'schema', 'btn-ghost': scope() !== 'schema' }} disabled={running()} onClick={() => setScope('schema')}>Schema</button>
          <button class="btn btn-sm" classList={{ 'btn-primary': scope() === 'server', 'btn-ghost': scope() !== 'server' }} disabled={running()} onClick={() => setScope('server')}>Server-wide</button>
        </div>
        <Show when={!isDbMode()}>
          <select class="input" style="max-width:130px;" value={direction()} onChange={(e) => setDirection(e.currentTarget.value)} disabled={running()}>
            <option value="both" selected={direction() === 'both'}>Both</option>
            <option value="inbound" selected={direction() === 'inbound'}>Inbound</option>
            <option value="outbound" selected={direction() === 'outbound'}>Outbound</option>
          </select>
        </Show>
        <button class="btn btn-sm btn-primary" disabled={running()} onClick={run}>{running() ? 'Scanning…' : '▶ Analyze'}</button>
        <span class="spacer" />
        <Show when={result()}>
          <input class="input" style="max-width:160px;" placeholder="Filter results…" value={filter()} onInput={(e) => setFilter(e.currentTarget.value)} />
        </Show>
      </div>

      <Show when={running()}>
        <div class="dep-status"><span class="dep-spinner" /> Scanning{scope() === 'server' ? ' every database' : ` ${database()}`}… this can take a moment.</div>
      </Show>

      <Show when={result()} fallback={<Show when={!running()}><div class="dep-empty">Pick a target and click Analyze. Tip: right-click an object or database in the browser.</div></Show>}>
        <div class="dep-stats muted">
          Scanned {result().scanned?.databases} database(s), {result().scanned?.objects} object(s) in {result().elapsedMs} ms
          <Show when={result().skipped}> · {result().skipped} body(ies) skipped (no permission)</Show>
        </div>
        <div class="dep-results">
          <Show when={direction() !== 'outbound' || isDbMode()}>
            <Section title="Referenced by (inbound)" list={result().inbound} groups={inbound()} />
          </Show>
          <Show when={direction() !== 'inbound' && !isDbMode()}>
            <Section title="References (outbound)" list={result().outbound} groups={outbound()} />
          </Show>
        </div>
      </Show>
    </div>
  );
}
